import { DeviceModels, MedisanteDevice } from '../../../../pages/Device/types';
import React, {
  FunctionComponent,
  useEffect,
  useReducer,
  useState,
} from 'react';
import ConfirmationDialog from '../../../../eliot-components/src/ConfirmationDialog/ConfirmationDialog';
import { Link } from '@material-ui/core';
import { allBluetoothDevices } from '../../../../pages/Device/constants';
import { useArchiveDeviceMutation } from '../../../../store/api-slice';
import { useConfig } from '../../../../eliot-components/src/ConfigProvider/hooks';
import { SelectedDevicesStatus } from '../TransferOwnership/types';
import {
  getGatewaySensors,
  getSelectedSensors,
} from '../DeviceLinking/provider';
import { selectedDevicesStatusReducer } from '../TransferOwnership/provider';
import { useGateways } from '../TransferOwnership/hooks';
import UnlinkAll from '../DeviceLinking/UnlinkAll';

interface Props {
  devices: MedisanteDevice[];
  isOpen: boolean;

  onClose?(): void;

  onCreated?(): void;
}

const ArchiveDevice: FunctionComponent<Props> = ({
  isOpen,
  onClose,
  devices,
}) => {
  const config = useConfig();
  const [archiveDevice, { isLoading: isUpdatingArchive }] =
    useArchiveDeviceMutation();
  const [isUnlinkAllOpened, setIsUnlinkAllOpened] = useState(false);
  const isAnyUnarchived = !!(devices && devices.find((d) => !d.isArchived));
  const isSensor =
    devices.filter((device) =>
      allBluetoothDevices.includes(device.model as DeviceModels)
    ).length === devices.length;

  const [selectedDevicesStatus, updateSelectedDevicesStatus] = useReducer(
    selectedDevicesStatusReducer,
    SelectedDevicesStatus.INITIAL
  );

  const {
    gateways,
    error: gwError,
    isLoading: gwLoading,
  } = useGateways(devices);

  useEffect(() => {
    updateSelectedDevicesStatus({
      devices,
      gateways,
      reset: true,
    });
  }, [devices, gateways]);

  const onUnlinkAllClose = (unlinkAll: boolean = false) => {
    updateSelectedDevicesStatus({
      devices,
      gateways,
      isUnlinked: unlinkAll,
    });

    setIsUnlinkAllOpened(false);
  };
  const gatewayOrSensorNeedUnlink =
    selectedDevicesStatus === SelectedDevicesStatus.GATEWAY_SELECTED ||
    selectedDevicesStatus === SelectedDevicesStatus.LINKED_SENSORS_SELECTED;
  const getSensorsToUnlink = () => {
    const selectedGateways = gateways.filter((g) =>
      devices.some((d) => d.id === g.id)
    );
    return selectedDevicesStatus ===
      SelectedDevicesStatus.LINKED_SENSORS_SELECTED
      ? getSelectedSensors(devices)
      : SelectedDevicesStatus.GATEWAY_SELECTED
      ? getGatewaySensors(selectedGateways)
      : [];
  };

  return (
    <div>
      <ConfirmationDialog
        isOpen={isOpen}
        onClose={onClose}
        onConfirm={() => {
          if (archiveDevice) {
            return archiveDevice({
              apiBaseURL: config.apiBaseURL,
              orgId: devices[0].owner,
              deviceIDs: devices.map((d) => d.id),
              isArchived: isAnyUnarchived,
            });
          }
        }}
        disabled={gatewayOrSensorNeedUnlink || gwLoading}
        isLoading={isUpdatingArchive}
        warningColor={gatewayOrSensorNeedUnlink ? 'error' : 'warning'}
        warningText={
          gatewayOrSensorNeedUnlink && isAnyUnarchived ? (
            <div>
              All the devices must be unlinked to archive device{' '}
              <Link onClick={() => setIsUnlinkAllOpened(true)}>Click here</Link>{' '}
              to unlink all devices!
            </div>
          ) : isAnyUnarchived && !isSensor ? (
            'Archiving removes any device webhook required for measurement routing'
          ) : (
            ''
          )
        }
        questionText={`Are you sure you want ${
          isAnyUnarchived ? 'Archive' : 'Unarchive'
        } this device ?`}
        confirmationText="Confirm"
        cancelText="Cancel"
        title={`${isAnyUnarchived ? 'Archive' : 'Unarchive'} device`}
      />
      <UnlinkAll
        open={isUnlinkAllOpened}
        onClose={onUnlinkAllClose}
        sensors={getSensorsToUnlink()}
        gateways={gateways}
        errorMessage={
          gwError && 'message' in gwError ? gwError.message : undefined
        }
        isLoading={gwLoading}
      />
    </div>
  );
};

export default ArchiveDevice;
